/**
 * Namespace containing constants for game round statuses.
 *
 * @namespace GameRoundStatusId
 * @description Defines the possible states a game round can be in during its lifecycle.
 * These statuses are used to track and manage the progression of game rounds across
 * different game types in the platform.
 */
export namespace GameRoundStatusId {
  /**
   * Indicates a round that has been created but not yet started.
   * @constant {bigint} Pending - Value: 1n
   */
  export const Pending = 1n;

  /**
   * Indicates a round that is in the process of loading resources or initializing.
   * @constant {bigint} Loading - Value: 2n
   */
  export const Loading = 2n;

  /**
   * Indicates a round that has successfully finished its normal execution cycle.
   * @constant {bigint} Completed - Value: 3n
   */
  export const Completed = 3n;

  /**
   * Indicates a round that was terminated before normal completion.
   * @constant {bigint} Cancelled - Value: 4n
   */
  export const Cancelled = 4n;

  /**
   * Indicates a round that is in the spinning phase (relevant for wheel-based games).
   * @constant {bigint} Spinning - Value: 5n
   */
  export const Spinning = 5n;

  /**
   * Indicates a round that has completed its spinning phase.
   * @constant {bigint} Spun - Value: 6n
   */
  export const Spun = 6n;

  /**
   * Indicates a round in the rising phase (relevant for games like Crash).
   * @constant {bigint} Rising - Value: 7n
   */
  export const Rising = 7n;

  /**
   * Indicates a round that has crashed (relevant for games like Crash).
   * @constant {bigint} Crashed - Value: 8n
   */
  export const Crashed = 8n;
}

/**
 * Type representing valid game round status values.
 *
 * @typedef {bigint} GameRoundStatusIdType
 * @description A type that represents any of the valid game round status constants.
 * This type is used for type checking when working with game round statuses.
 */
export type GameRoundStatusIdType =
  (typeof GameRoundStatusId)[keyof typeof GameRoundStatusId];
